import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ReactSortable } from "react-sortablejs";
import { Accordion, AccordionBody, AccordionTrigger } from '@/react/components/Accordion';
import Spinner from '@/react/components/Spinner';
import Swal from '@/react/components/Swal'
import EditableTextInput from '@/react/components/EditableTextInput';

const GsListWrapper = ({ isFrom = false }) => {
	const queryClient = new QueryClient();

	return (
		<QueryClientProvider client={queryClient}>
			<QuickApplyGs isFrom={isFrom} />
		</QueryClientProvider>
	)
}

const QuickApplyGs = ({ isFrom }) => {
	const [list, setList] = useState([]);

	const fetchGsList = async () => {
		// MS_ID Exists globally
		const response = await ApiClient.get(`${BASE_URL}my-schedule/${MS_ID}/edit-quick-apply-gs${isFrom ? '?is_gs_from=true' : ''}`);
		return response.data.data.map(x => ({ ...x, key: Math.random().toString(36).substring(2, 9) }));
	}

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['quickApplyGsList', MS_ID],
		queryFn: fetchGsList,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		staleTime: Infinity,
		cacheTime: Infinity
	});

	useEffect(() => {
		if (data) {
			setList(data);
		}
	}, [data])

	const handleAddNew = () => {
		if (list.length >= 10) {
			toast.error('You can only have a maximum of 10 Quick applies.');
			return;
		}
		const newGs = {
			name: `Quick apply ${list.length + 1}`,
			active: true,
			countryID: [],
			stateID: [],
			dataTypes: [],
			index: -1,
			na: "off",
			statutory: "off",
			unspecified: "off",
			key: Math.random().toString(36).substring(2, 9),
		};
		setList([newGs, ...list]);
	}

	const handleReset = async () => {
		const r = await Swal.fire({
			title: `Restore Quick applies?`,
			text: `Are you sure you want to restore the Quick applies back to their last saved version? All unsaved changes will be lost.`,
			icon: 'warning',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Reset'
		})

		if (!r.isConfirmed) return;

		toast.promise(
			(async () => {
				await refetch();
			})(),
			{
				loading: 'Resetting Quick applies...',
				success: 'Quick applies have been reset',
				error: 'There was an error resetting the Quick applies'
			}
		);
	}

	return (
		<>
			<div className='modal__content-wrapper-header !p-0'>
				<h2>Quick apply the Golden standard {isFrom ? 'from' : ''}</h2>
			</div>

			<div className='flex gap-2 ml-auto'>
				<button onClick={handleReset} className='btn btn-outline-primary'><span className='font-MaterialIconsNew text-lg mr-1 font-normal'>refresh</span> Restore</button>
				<button onClick={handleAddNew} className='btn btn-primary animation-none animation-pop-out'>Add new Quick apply</button>
			</div>

			<div className='flex flex-col border-normal border-grey rounded-primary p-4 pr-2'>
				<div className='max-h-[50vh] overflow-y-auto transition-all flex flex-col'>
					<GsList list={list} setList={setList} isLoading={isLoading} isFrom={isFrom} />
				</div>
			</div>
		</>
	)
}

const GsList = ({ list, setList, isLoading, isFrom }) => {

	const handleDelete = async (key, name) => {
		const r = await Swal.fire({
			title: `Delete this Quick apply?`,
			text: `Are you sure you want to delete ${name}? You won't be able to revert this!`,
			icon: 'delete',
			showCancelButton: true,
			cancelButtonText: 'Cancel',
			confirmButtonText: 'Delete'
		})

		if (!r.isConfirmed) return;

		const updatedList = list.filter(gs => gs.key !== key);
		setList(updatedList);
	}

	return (
		<>
			{isLoading &&
				<div className='h-full w-full place-content-center'>
					<Spinner />
				</div>
			}

			{!isLoading && list.length === 0 &&
				<p className='text-secondaryHead text-center text-grey-text font-bold my-auto'>
					No Quick applies found <br></br>
					<span className='text-article font-normal mt-2'>Please add a new Quick apply, or proceed if you wish to remove all Quick applies from this Schedule</span>
				</p>}
			{
				!isLoading && list.length > 0 && (<ReactSortable list={list} setList={setList} className='h-full [&_.chosen-item]:shadow-lg [&_.chosen-item]:border-thin [&_.chosen-item]:rounded-primary [&_.chosen-item]:!border-primary empty:hidden'
					handle='.item-handle'
					animation={200}
					ghostClass='invisible'
					chosenClass='chosen-item'
					easing='cubic-bezier(1,0,0,1)'
				>
					{list.map(gs => <Gs key={gs.key} gs={gs} handleDelete={handleDelete} isFrom={isFrom} />)}
				</ReactSortable>)
			}
		</>
	);
}

const Gs = ({ gs, handleDelete, isFrom }) => {
	const { active, countryID, dataTypes, stateID, key, name } = gs;
	const jurisdictionRef = useRef(null);
	const dataTypeRef = useRef(null);

	const checkboxes = [
		{
			name: "unspecified",
			info: "Sometimes the law says â€œkeepâ€ or â€œkeep no longer than necessaryâ€ without specifying how long. Applying the golden standard to an unspecified result means that your users will never have to guess how long they should keep their records as the golden standard is always specified.",
			label: "Apply to unspecified retention obligations"
		}, {
			name: "na",
			info: `Sometimes there is just no law or no retention obligation. In that case there is no retention period to mention. Applying the golden standard to a N/A result means that your users will never have to guess how long they should keep their records as the golden standard is always specified.
					<br>
					<br>
					NOTE: N/A also sometimes means that there is no obligation or it is even prohibited to keep records in some countries or states. In such case, please make sure to exclude those jurisdictions under â€œselect jurisdictionsâ€
					`,
			label: "Apply to N/A results"
		}, {
			name: "statutory",
			info: "Statute of limitations technically do not tell you to keep any data or records. The from trigger of a statute of limitation can therefore be confusing as they often refer to the moment of the damages or when someone became aware of them. You can replace these unhelpful legal froms with the same from as in your golden standard. You can change the golden standard from at any time.",
			label: "Apply to statute of limitations"
		},{
			name: "minimum",
			info: `
				Apply the golden standard to minimum retention periods. When a law specifies a minimum retention period, this option will ensure the golden standard is used as the baseline.
			`,
			label: "Apply to minimum"
		},{
			name: "maximum",
			info: `
				Apply the golden standard to maximum retention periods. When a law specifies a maximum retention period, this option will ensure the golden standard is used as the upper limit.
			`,
			label: "Apply to maximum"
		}
	];

	useEffect(() => {
		const url = `${API_URL}list/my-schedule/${MS_ID}`

		async function init() {
			if (jurisdictionRef.current) {
				const jDropdown = new MultiLayerDropdown(jurisdictionRef.current, {
					searchPlaceholder: 'Search jurisdictions',
					levels: ['Country', 'State'],
					keys: ['Country', 'State'],
				})

				const r = await ApiClient.get(`${url}/jurisdictions`)
				jDropdown.initializeData(r.data)
				if (countryID)
					if (countryID.length && countryID.length > 0 || stateID.length &&
						stateID.length > 0) {
						jDropdown.selectItems({
							Country: countryID,
							State: stateID,
						})
					}
			}

			if (dataTypeRef.current) {
				const dtDropdown = new MultiLayerDropdown(dataTypeRef.current, {
					searchPlaceholder: 'Search data classes & types',
					levels: ['Data Class', 'Data Type'],
					keys: ['Data Class', 'Data Type'],
				})

				const r = await
					ApiClient.get(`${url}/data-class-data-types-with-id`)

				const {
					identifier,
					data
				} = r.data
				dtDropdown.setLevels(identifier)
				dtDropdown.setPlaceholder(`Search ${identifier.join(' & ')}`)
				dtDropdown.initializeData(data)

				if (dataTypes)
					if (dataTypes.length && dataTypes.length > 0) {
						dtDropdown.selectItems({
							'Data Type': dataTypes,
						})
					}
			}
		}
		init();
	}, [])

	return (
		<Accordion className={`flex flex-col py-2 pr-2 [&:not([is-open="true"]):not(:last-child)]:border-b-thin border-grey gs-list-item`} >
			<AccordionTrigger className="hover:cursor-pointer flex items-center">
				<div className='sortable-item flex gap-2 items-center hover:cursor-auto' onClick={(e) => e.stopPropagation()}>
					<span className='item-handle' onClick={e => e.stopPropagation}>drag_indicator</span>
					<input
						type="checkbox"
						name="active"
						defaultChecked={active}
						className="w-4 h-4"
					/>
					<EditableTextInput initialValue={name} name="gs-item-name" className="font-semibold text-lg flex-1" />
				</div>
			</AccordionTrigger>

			<AccordionBody>
				<div className='border-normal border-grey rounded-primary p-4 flex flex-col gap-6 mt-2'>
					<p className="text-sub2 font-semibold">
						Decide which result you want to apply the Golden standard{isFrom ? 'from ' : ''} to
					</p>

					<div className="justify-between flex gap-10">
						<div className="flex flex-col gap-2 flex-[6_1_0%]">
							<p className="font-semibold text-label">Please select which jurisdictions you want to apply the Golden standard to</p>
							<p className="text-grey-text">
								It is possible to restrict the application of the golden standard to certain jurisdictions. Select the countries or
								states which are where you want to apply the Golden standard to jurisdiction specific results.
							</p>
						</div>
						<div className="flex flex-col gap-2 flex-[4_1_0%]">
							<div className="w-full multi-layer-container jdd" ref={jurisdictionRef}>
							</div>
						</div>
					</div>
					<div className="justify-between flex gap-10">
						<div className="flex flex-col gap-2 flex-[6_1_0%]">
							<p className="font-semibold text-label">Please select which data types you want to apply the Golden standard to</p>
							<p className="text-grey-text">
								It is possible to restrict the application of the Golden standard to data types. Select the data type with regards
								to which you want to apply the golden standard to jurisdiction specific results.
							</p>
						</div>
						<div className="flex flex-col gap-2 flex-[4_1_0%] ">
							<div className="w-full multi-layer-container dtdd" data-parent-select-all="true" ref={dataTypeRef}>
							</div>
						</div>
					</div>
					<hr className="-mb-1 -mt-1" />
					<div className="flex flex-col gap-4 w-full">
						{checkboxes.map(checkbox =>
							<FilterOption
								{...checkbox}
								key={checkbox.name}
								active={gs[checkbox.name] === 'on'}
							/>
						)}
					</div>
					<div className='flex gap-2'>
						<button className='ml-auto btn btn-outline-secondary' onClick={() => handleDelete(key, name)}>Delete</button>
					</div>
				</div>
			</AccordionBody>
		</Accordion>
	)
}

const tippyOptions = {
	theme: 'retention-schedule',
	placement: 'right-start',
	interactive: true,
}

function FilterOption({ label, name, info, active }) {
	return (
		<label className="flex items-center gap-2 hover:cursor-pointer">
			<input
				type="checkbox"
				className="mt-[1px] gs_op"
				name={name}
				defaultChecked={active}
			/>

			<span className="font-semibold">{label}</span>
			<span
				className="font-MaterialIcons text-primary text-xl"
				data-tippy-content={info}
				data-tippy-options={JSON.stringify(tippyOptions)}
			>
				info
			</span>
		</label>
	)
}

export default GsListWrapper;